// =============================================================================
// Step 1 — Story video (PUBLIC, funnel entry)
// =============================================================================
// The funnel now starts here, before auth. A visitor — whether they arrived
// from an invite link (/i/:slug → /welcome?ref=…) or the landing page — lands
// on the story video first. They can watch it or skip; either way the next
// step is sign-in (/login), and after Google/email auth they land on the
// signup info form (/signup).
//
//   - Public: no SignedIn gate. Anonymous visitors see the video.
//   - A returning, signed-in supporter is sent straight to /status.
//   - The referrer slug (?ref=…) is persisted to localStorage for attribution
//     and read later by /signup.
// =============================================================================

import { useEffect, useState } from "react"
import { track } from "@/lib/track"
import { useNavigate, useSearchParams, Navigate } from "react-router-dom"
import { useUser } from "@clerk/clerk-react"
import { Button } from "@/components/ui/button"
import { PageShell } from "@/components/brand/PageShell"
import { StepIndicator } from "@/components/brand/StepIndicator"
import { useApi } from "@/lib/api"
import { assetUrl } from "@/lib/utils"
import { resolveRef, persistRef } from "@/lib/referrer"
import { ArrowLeft, ArrowRight } from "lucide-react"

// The story video (Matan) lives in the elhadegel-web-assets R2 bucket, served
// via VITE_ASSETS_BASE_URL — media never lives in the repo (keeps clones lean
// + stays under Cloudflare Pages' 25 MB static-file limit).
const STORY_VIDEO = assetUrl("story-video-0907.mp4")

interface MeResponse {
  supporter: { id: string; inviteSlug: string } | null
}

export function WelcomePage() {
  const navigate = useNavigate()

  // Instrumentation only — see src/lib/track.ts. On 21 Aug the numbers said
  // HALF of everyone who arrives never reaches the login button, and this page
  // is what stands between the two. Nobody had ever measured what it costs.
  useEffect(() => {
    track("welcome_view")
  }, [])
  const [params] = useSearchParams()
  const apiCall = useApi()
  const { isLoaded, user } = useUser()
  const [hasSupporter, setHasSupporter] = useState<boolean | null>(null)

  const referrerSlug = resolveRef(params.toString())

  // Persist the referrer slug (localStorage + cookie) so attribution survives
  // the auth round-trip and the mobile in-app-browser -> Safari hop.
  useEffect(() => {
    const ref = params.get("ref")
    if (ref) persistRef(ref)
  }, [params])

  // If a signed-in visitor already completed signup, skip the funnel.
  useEffect(() => {
    if (!isLoaded || !user) return
    apiCall<MeResponse>("/api/me")
      .then((d) => setHasSupporter(!!d.supporter))
      .catch(() => setHasSupporter(false))
  }, [isLoaded, user, apiCall])

  // Signed-in visitor: don't flash the video/funnel while we resolve whether
  // they already completed signup — show a brief loader, then route.
  if (isLoaded && user && hasSupporter === null) {
    return (
      <PageShell bg="subtle">
        <div className="max-w-md mx-auto px-6 py-20 text-center text-sm text-[#6B7280]">
          טוען…
        </div>
      </PageShell>
    )
  }

  if (hasSupporter === true) return <Navigate to="/status" replace />

  // Both skip and continue lead to sign-in; carry the slug in the URL too so it
  // is never dependent on storage alone.
  const goToSignIn = () => {
    track("continue_tap")
    navigate(referrerSlug ? `/login?ref=${encodeURIComponent(referrerSlug)}` : "/login")
  }

  return (
    <PageShell bg="subtle" header={<StepIndicator current={1} total={3} />}>
      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-10">
        <div className="mx-auto w-full max-w-xs sm:max-w-sm text-center">
          {referrerSlug && (
            <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-[#1B2D52]/5 px-4 py-1.5 ring-1 ring-[#1B2D52]/10">
              <span className="text-xs font-semibold text-[#1B2D52] tracking-wide">
                הוזמנת על ידי חבר/ה
              </span>
            </div>
          )}
          <h1 className="text-2xl lg:text-3xl font-extrabold text-[#0F1B36] mb-2 leading-tight">
            סרטון של דקה אחת — אז דגל
          </h1>
          <p className="text-sm text-[#4B5563] mb-6 leading-relaxed">
            צפ/י בסרטון, ואז התחבר/י והוסף/י את הדגל שלך למפה.
          </p>
          {/* The source video is 9:16 (1080×1920). We give the box that exact ratio on
              EVERY screen so the whole frame shows with no cropping and no black bars —
              mobile and desktop alike. object-top is a harmless safety anchor. */}
          <div className="w-full rounded-2xl overflow-hidden bg-black aspect-[9/16] shadow-xl shadow-[#1B2D52]/10">
            <video
              src={STORY_VIDEO}
              controls
              playsInline
              onPlay={() => track("video_play")}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="mt-6 flex items-center justify-between gap-4">
            <Button
              size="lg"
              variant="ghost"
              onClick={() => navigate("/")}
              className="group text-[#6B7280] hover:text-[#1B2D52]"
            >
              <ArrowRight className="size-5 transition-transform group-hover:translate-x-1" />
              חזרה
            </Button>
            <Button size="lg" onClick={goToSignIn} className="group">
              המשך לכניסה
              <ArrowLeft className="size-5 transition-transform group-hover:-translate-x-1" />
            </Button>
          </div>
        </div>
      </main>
    </PageShell>
  )
}
